//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { FileUploader } from '@campaigns/components/shared/file-uploader/FileUploader';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { MAX_IMAGE_SIZE_BYTES } from '@campaigns/constants/SamplingImageConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  URL_REGEX_RULE,
  isProductImageContentTypeValid,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useImageUpload } from '@campaigns/hooks/useImageUpload';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { Alert } from '@components/atoms/alert/Alert';
import {
  PrimaryButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CaptionText } from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';

interface ProductImageUploadProps {
  error?: string;
  isDisabled: boolean;
  isViewOnly: boolean;
  onClearImage: () => void;
  onFileSelect: (file: File | undefined) => void;
  onPreview: () => void;
  onToggleInputMode: () => void;
  updateVariantImages: (url: string) => void;
}

export const ProductImageUpload = ({
  error,
  isDisabled = false,
  isViewOnly = false,
  onClearImage,
  onFileSelect,
  onPreview,
  onToggleInputMode,
  updateVariantImages,
}: ProductImageUploadProps) => {
  const { isImageForAllVariants } = useProductModal();
  const { clearErrors, getValues, trigger } =
    useFormContext<EditableBVSamplingProduct>();
  const [isImageUrlInputMode, setIsImageUrlInputMode] = useState(false);

  const { error: imageUploadError, handleFilesSelected } = useImageUpload({
    onClearImage,
    onFileSelect,
  });

  const displayError = error ?? imageUploadError;

  const handlePreview = async () => {
    const isValid = await trigger('imgUrl');
    if (isValid) {
      onPreview();
    }
  };

  const toggleInputMode = () => {
    onClearImage();
    setIsImageUrlInputMode(!isImageUrlInputMode);
    onToggleInputMode();
  };

  return (
    <Container>
      {isImageUrlInputMode ? (
        <UrlInputContainer>
          <FormField
            inputType='textarea'
            isViewOnly={isViewOnly}
            label='Image web address'
            name='imgUrl'
            onValueChange={async (value: string) => {
              clearErrors('imgUrl');
              if (!value) {
                return;
              }
              if (isProductImageUrlValid(value)) {
                if (
                  (await isProductImageContentTypeValid(value)) &&
                  isImageForAllVariants &&
                  getValues('imgUrl') === value
                ) {
                  updateVariantImages(value);
                }
              }
            }}
            placeholder='https://your-awesome-website.com/030524.png'
            rules={{
              required: {
                value: true,
                message: 'Please enter a product image URL',
              },
              pattern: {
                value: URL_REGEX_RULE,
                message: 'Please enter a valid URL',
              },
              validate: {
                validContentType: async (value) => {
                  if (!value) {
                    return true;
                  }
                  const isValid = await isProductImageContentTypeValid(value);
                  return (
                    isValid || 'Invalid product image URL or unsupported format'
                  );
                },
              },
            }}
          />
          <PreviewButton
            onClick={handlePreview}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_PREVIEW_IMAGE_BUTTON
            }
          >
            Preview
          </PreviewButton>
        </UrlInputContainer>
      ) : (
        <>
          {/*
            This form can switch between two input modes: file upload and image URL textarea.
            React Hook Form loses validation state for a field if it is unmounted from the DOM.
            To ensure validation errors for the image URL persist and display correctly when switching between modes,
            we keep the textarea field for 'imgUrl' hidden (but mounted) when using file upload.
            This allows error messages to be shown consistently, regardless of which input method is active.
          */}
          <HiddenFormField>
            <FormField
              inputType='textarea'
              label='Image web address'
              name='imgUrl'
              rules={{
                required: {
                  value: true,
                  message: 'Please upload a product image',
                },
                validate: {
                  validUrl: async (value) => {
                    if (!value) {
                      return true;
                    }
                    const isValid = await isProductImageContentTypeValid(value);
                    return (
                      isValid ||
                      'Invalid product image URL or unsupported format'
                    );
                  },
                },
              }}
            />
          </HiddenFormField>
          {isDefined(displayError) && (
            <Alert type='error'>{displayError}</Alert>
          )}
          <FileUploader
            actionText='Click to select'
            existingAssetUrls={[]}
            isDisabled={isDisabled || isViewOnly}
            maxTotalSize={MAX_IMAGE_SIZE_BYTES}
            onFilesSelected={handleFilesSelected}
            shouldAcceptMultipleFiles={false}
            supportedFileTypes={SUPPORTED_IMAGE_MIME_TYPES}
            title='Upload image'
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_UPLOAD_IMAGE
            }
          />
        </>
      )}
      {!isViewOnly && (
        <ToggleContainer>
          <CaptionText>or</CaptionText>
          {isImageUrlInputMode ? (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_UPLOAD_IMAGE_TOGGLE_BUTTON
              }
            >
              <StyledIcon name='upload' />
              <span>Upload an image</span>
            </PrimaryLinkButton>
          ) : (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_IMAGE_URL_TOGGLE_BUTTON
              }
            >
              Enter image link instead
            </PrimaryLinkButton>
          )}
        </ToggleContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const UrlInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const StyledIcon = styled(Icon)`
  margin-right: ${({ theme }) => theme.spacings[1]};
  vertical-align: middle;
`;

const PreviewButton = styled(PrimaryButton)`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

const HiddenFormField = styled.div`
  display: none;
`;
